import type { ProductResponse } from "@/app/api/types/product";
import type { Partner, PartnerContact } from "@/app/api/types/partner";
import {
  getPrimaryAddress,
  getPrimaryContact,
  type AddressParts,
} from "@/lib/partner/primaryContact";
import type { RankedSearchConfig } from "@stockline/product-search";

// Searchable fields must align with what's rendered in partner rows; both
// surfaces resolve "primary" the same way via `@/lib/partner/primaryContact`.

const firstNonEmpty = (
  xs: readonly (string | null | undefined)[] | null | undefined,
): string => {
  if (!xs) return "";
  for (const x of xs) {
    if (x && x.trim().length > 0) return x.trim();
  }
  return "";
};

const fullName = (c: PartnerContact | null | undefined): string =>
  c ? `${c.name ?? ""} ${c.surname ?? ""}`.trim() : "";

// --- Product search config ---

export const PRODUCT_FUSE_OPTIONS = {
  keys: [
    { name: "name", weight: 2 },
    { name: "short_name", weight: 1.5 },
    { name: "sku", weight: 1.5 },
    { name: "code", weight: 1 },
    { name: "labels.name", weight: 1 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
};

export const PRODUCT_SEARCH_CONFIG: RankedSearchConfig<ProductResponse> = {
  codeField: (p) => p.sku,
  textFields: (p) => [
    p.name,
    p.short_name,
    p.sku,
    p.code,
    ...(p.labels?.map((l) => l.name) ?? []),
  ],
  fuseOptions: PRODUCT_FUSE_OPTIONS,
};

// --- Partner search config ---

export const PARTNER_FUSE_OPTIONS = {
  keys: [
    { name: "name", weight: 2 },
    { name: "usage_name", weight: 1.5 },
    { name: "code", weight: 1 },
    {
      name: "_primaryCity",
      weight: 0.7,
      getFn: (p: Partner) => getPrimaryAddress(p)?.city ?? "",
    },
    {
      name: "_primaryStreet",
      weight: 0.4,
      getFn: (p: Partner) => getPrimaryAddress(p)?.address_1 ?? "",
    },
    {
      name: "_primaryZip",
      weight: 0.4,
      getFn: (p: Partner) => getPrimaryAddress(p)?.zip_code ?? "",
    },
    {
      name: "_primaryContactName",
      weight: 0.6,
      getFn: (p: Partner) => fullName(getPrimaryContact(p)),
    },
    {
      name: "_primaryContactPhone",
      weight: 0.5,
      getFn: (p: Partner) => firstNonEmpty(getPrimaryContact(p)?.phones),
    },
    {
      name: "_primaryContactEmail",
      weight: 0.7,
      getFn: (p: Partner) => firstNonEmpty(getPrimaryContact(p)?.emails),
    },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
};

export const PARTNER_SEARCH_CONFIG: RankedSearchConfig<Partner> = {
  codeField: (p) => p.code,
  textFields: (p) => {
    const a = getPrimaryAddress(p);
    const c = getPrimaryContact(p);
    const name = fullName(c);
    return [
      p.name,
      p.usage_name,
      p.code,
      a?.address_1 ?? null,
      a?.city ?? null,
      a?.zip_code ?? null,
      name.length > 0 ? name : null,
      firstNonEmpty(c?.phones) || null,
      firstNonEmpty(c?.emails) || null,
    ];
  },
  fuseOptions: PARTNER_FUSE_OPTIONS,
};

// --- Transport recipient search config ---
//
// In transport mode the `products` table stores destinations (places /
// drop-off points), not SKUs: `short_name`, `labels` are empty and `sku`
// is a duplicate of `code`. The interesting detail (street / city / zip /
// country) lives in `product_metadata`. The transport recipient picker
// uses this config so its search field set mirrors the sender picker's
// `PARTNER_SEARCH_CONFIG` (code + name + address) — see
// `docs/superpowers/specs/2026-06-01-recap-transport-page-design.md` §3.
interface TransportRecipientMetadata {
  address_1?: string | null;
  code_postal?: string | null;
  ville?: string | null;
  pays?: string | null;
}

const recipientMeta = (p: ProductResponse): TransportRecipientMetadata =>
  (p.product_metadata as TransportRecipientMetadata | null | undefined) ?? {};

// `product_metadata` is cast to typed string fields, but the raw JSON can hold
// a numeric value (e.g. `code_postal: 75001`). Coerce to a non-empty string so
// every metadata read is uniform: search string ops (`tokenMatchFilter`'s
// `.toLowerCase()`, Fuse) never throw on a number, and the displayed address
// renders a numeric postal code instead of silently dropping it. Single
// chokepoint shared by the search configs and `formatRecipientAddressOneLine`.
const metaStr = (v: unknown): string | null => {
  if (typeof v === "string") return v.trim().length > 0 ? v : null;
  if (typeof v === "number") return String(v);
  return null;
};

/**
 * One-line recipient (destination) address from `product_metadata`. Mirrors the
 * `customAddressLine` format used by `EntitySelectWithTags` for custom entities:
 * `address_1, "zip city", pays`. Returns null when no part is present, so the
 * picker can fall back to a "—" placeholder. The metadata-field knowledge lives
 * here next to `recipientMeta` rather than leaking into the picker.
 */
export const formatRecipientAddressOneLine = (
  product: ProductResponse | undefined,
): string | null => {
  if (!product) return null;
  const m = recipientMeta(product);
  // Coerce each part via `metaStr`: a numeric `code_postal` (e.g. 13007) must
  // be rendered as a string, not silently dropped, while non-string junk is
  // discarded. `metaStr` already returns null for empty/whitespace strings.
  const cityPart = [metaStr(m.code_postal), metaStr(m.ville)]
    .filter((p): p is string => p !== null)
    .join(" ");
  const formatted = [metaStr(m.address_1), cityPart, metaStr(m.pays)]
    .filter((p): p is string => p !== null && p.length > 0)
    .join(", ")
    .trim();
  return formatted.length > 0 ? formatted : null;
};

/**
 * Structured variant of {@link formatRecipientAddressOneLine} for
 * space-constrained rows: `street` (address_1) may be ellipsized by the
 * display, `tail` (`"zip city, pays"`) stays fully visible. Consumed by the
 * recipient picker via `EntityAddressLine`.
 */
export const getRecipientAddressParts = (
  product: ProductResponse | undefined,
): AddressParts | null => {
  if (!product) return null;
  const m = recipientMeta(product);
  const cityPart = [metaStr(m.code_postal), metaStr(m.ville)]
    .filter((p): p is string => p !== null)
    .join(" ");
  const tail = [cityPart, metaStr(m.pays)]
    .filter((p): p is string => p !== null && p.length > 0)
    .join(", ");
  const street = metaStr(m.address_1);
  if (!street && tail.length === 0) return null;
  return { street, tail: tail.length > 0 ? tail : null };
};

export const TRANSPORT_RECIPIENT_FUSE_OPTIONS = {
  keys: [
    { name: "name", weight: 2 },
    { name: "code", weight: 1.5 },
    {
      name: "_meta_address_1",
      weight: 0.4,
      getFn: (p: ProductResponse) => metaStr(recipientMeta(p).address_1) ?? "",
    },
    {
      name: "_meta_ville",
      weight: 0.7,
      getFn: (p: ProductResponse) => metaStr(recipientMeta(p).ville) ?? "",
    },
    {
      name: "_meta_zip",
      weight: 0.4,
      getFn: (p: ProductResponse) =>
        metaStr(recipientMeta(p).code_postal) ?? "",
    },
    {
      name: "_meta_pays",
      weight: 0.3,
      getFn: (p: ProductResponse) => metaStr(recipientMeta(p).pays) ?? "",
    },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
};

export const TRANSPORT_RECIPIENT_SEARCH_CONFIG: RankedSearchConfig<ProductResponse> =
  {
    codeField: (p) => p.code,
    textFields: (p) => {
      const m = recipientMeta(p);
      return [
        p.name,
        p.code,
        metaStr(m.address_1),
        metaStr(m.ville),
        metaStr(m.code_postal),
        metaStr(m.pays),
      ];
    },
    fuseOptions: TRANSPORT_RECIPIENT_FUSE_OPTIONS,
  };
